import { Dropdown } from 'antd';
import i18next from 'i18next';
import { FC, ReactNode, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { resources } from '../../../commons/constants';
import useLanguage from '../../../hooks/useLanguage';
import { storeName } from '../../../redux/models/layout.model';
import { storeName as userModelStoreName } from '../../../redux/models/user.model';
import { authorize } from '../../../services/auth';
import { getRedirectUrl, removeAccessToken } from '../../../utils/storage';

import './styles.scss';

type LayoutMainPropsTypes = {
  children: ReactNode;
};

const LayoutMain: FC<LayoutMainPropsTypes> = ({ children }) => {
  const history = useHistory();
  const { setLanguage } = useLanguage();

  const { navbarExpand } = useSelector((state) => state[storeName].navbarConfig);
  const user = useSelector((state) => state[userModelStoreName].user);
  const dispatch = useDispatch();

  const getUser = () => {
    dispatch[userModelStoreName]
      .getUserDetailAsync()
      .then((userResult: any) => {
        if (!userResult) {
          authorize();
        } else {
          const redirectUrl = getRedirectUrl();
          if (redirectUrl) {
            const url = new URL(redirectUrl);
            history.push(`${url.pathname}${url.search}`);
          }
        }
      })
      .catch((err: any) => {
        console.error('useAuth:', err);
        authorize();
      });
  };

  return (
    <div className="clik-inventory__main">
      <header className="main__header">
        <div className="header__navbar-action">
          <i
            className="icon-menu_hamburger_24x24 header__action"
            onClick={() => dispatch[storeName].toggleNavbarConfig(!navbarExpand)}
          />
        </div>
        <div className="header__actions">
          <Dropdown
            menu={{
              items: Object.keys(resources).map((key) => {
                const lang = resources[key];
                return {
                  onClick: () => setLanguage(lang.key),
                  key: lang.key,
                  icon: lang.icon,
                  label: lang.name,
                };
              }),
            }}
            placement="bottom"
          >
            <div className="action__language">
              {resources[i18next.language] ? (
                <>
                  {resources[i18next.language].icon}
                  <div className="language__short-name">
                    {resources[i18next.language].shortDisplayName}
                  </div>
                </>
              ) : null}
            </div>
          </Dropdown>
          <Dropdown
            menu={{
              items: user
                ? [
                    {
                      key: 'authorized',
                      icon: <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-500" />,
                      onClick: () => {
                        removeAccessToken();
                        const url = `${process.env.REACT_APP_OAUTH_DOMAIN}/exit?redirect_url=${window.location.href}`;
                        window.location.href = url;
                      },
                      label: 'Logout',
                    },
                  ]
                : [
                    {
                      key: 'login',
                      icon: <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-500" />,
                      onClick: () => {
                        getUser();
                      },
                      label: 'Login',
                    },
                  ],
            }}
            placement="topRight"
          >
            <div className="action__user">
              <div className="user__info">
                <span className="info__name">{user?.name}</span>
                <span className="info__role">{user?.email}</span>
              </div>
              <div className="user__avatar">{user?.avatar}</div>
            </div>
          </Dropdown>
        </div>
      </header>
      <div className="main__content">{children}</div>
      <div className="main__footer">
        <span>Powered by</span>
        <i className="icon-logo_clik_44x18" />
      </div>
    </div>
  );
};

const LayoutMainMemo = memo(LayoutMain);
export { LayoutMainMemo as LayoutMain };
